<script setup>
/**
 * The packages of a quote as one table: a row per kind - how many alike, size
 * and weight, and for freight its type, class (Auto: from its density) and
 * whether it stacks. Edits the array it is given.
 */
import {computed, watch} from "vue";
import store from "@/store";
import helpers from "@/lib/helpers";
import {densityClass, density} from "@/lib/freight";
import {breakpointsBootstrapV5, useBreakpoints} from "@vueuse/core/index";

const props = defineProps({
  value: {type: Array, required: true},
  freight: Boolean,
})
const emits = defineEmits(['next'])

const rows = computed(() => props.value)

// a phone: each row a small card of full-size fields rather than a squeezed table row
const narrow = useBreakpoints(breakpointsBootstrapV5).smaller('md')
const lists = computed(() => store.state.settings.defaults.freight || {handling_types: [], classes: []})
const scales = computed(() => store.state.settings.defaults.scales || [])

const blank = () => props.freight
    ? {quantity: 1, handling_type: 'pallet', freight_class: null, stackable: false}
    : {quantity: 1}

const add = () => {
  const last = rows.value[rows.value.length - 1]
  rows.value.push(last ? {...last, quantity: 1} : blank())
}
const remove = (i) => rows.value.splice(i, 1)

// never an empty table: a cleared list starts again with one row
watch(rows, (r) => {
  if (!r.length)
    r.push(blank())
}, {immediate: true})

const auto = (r) => densityClass(r.pkg_length, r.pkg_width, r.pkg_height, r.pkg_weight)
const lbft = (r) => density(r.pkg_length, r.pkg_width, r.pkg_height, r.pkg_weight)

const weigh = async (r, scale) => {
  const {weight} = await helpers.asyncAction('/api/scale?scale=' + scale, 'GET')
  r.pkg_weight = weight
}

// Enter on the last weight quotes; anywhere else it moves on
const enter = (i) => {
  if (i === rows.value.length - 1)
    emits('next')
}
</script>

<template>
  <div v-if="narrow">
    <div v-for="(r, i) in rows" :key="i" class="unit-card mb-2">
      <div class="d-flex align-items-center mb-2">
        <b-input-group size="sm" prepend="Qty" class="qty-group mr-2">
          <b-form-input type="number" min="1" max="50" v-model.number="r.quantity"/>
        </b-input-group>
        <template v-if="freight">
          <b-form-select size="sm" class="mr-2" v-model="r.handling_type" :options="lists.handling_types"/>
          <b-form-checkbox v-model="r.stackable" class="text-nowrap mr-2">Stack</b-form-checkbox>
        </template>
        <b-link class="text-danger ml-auto px-1" v-if="rows.length > 1" @click="remove(i)" title="Remove">
          <font-awesome-icon :icon="['far', 'trash-can']"/>
        </b-link>
      </div>
      <b-input-group size="sm" class="mb-2" append="in">
        <b-form-input type="number" inputmode="decimal" min="0" v-model.number="r.pkg_length" placeholder="L"/>
        <b-form-input type="number" inputmode="decimal" min="0" v-model.number="r.pkg_width" placeholder="W"/>
        <b-form-input type="number" inputmode="decimal" min="0" v-model.number="r.pkg_height" placeholder="H"/>
      </b-input-group>
      <div class="d-flex">
        <b-input-group size="sm" class="flex-fill" :class="{'mr-2': freight}">
          <b-form-input type="number" inputmode="decimal" min="0" step="any" v-model.number="r.pkg_weight" placeholder="Weight"
                        @keydown.enter="enter(i)"/>
          <b-input-group-append is-text>lb</b-input-group-append>
          <b-input-group-append v-if="scales.length">
            <b-dropdown size="sm" variant="outline-secondary" right>
              <template #button-content><font-awesome-icon icon="fa-solid fa-balance-scale"/></template>
              <b-dropdown-item v-for="s in scales" :key="s" @click="weigh(r, s)">Scale: {{ s }}</b-dropdown-item>
            </b-dropdown>
          </b-input-group-append>
        </b-input-group>
        <div v-if="freight" class="flex-fill">
          <b-form-select size="sm" v-model="r.freight_class">
            <b-form-select-option :value="null">Class: Auto{{ auto(r) ? ' (' + auto(r) + ')' : '' }}</b-form-select-option>
            <b-form-select-option v-for="c in lists.classes" :key="c" :value="c">Class {{ c }}</b-form-select-option>
          </b-form-select>
        </div>
      </div>
      <div class="small text-muted text-right density" v-if="freight">{{ lbft(r) ? lbft(r) + ' lb/ft³' : '\u00a0' }}</div>
    </div>
    <b-link class="small" @click="add">
      <font-awesome-icon icon="fa-solid fa-plus" class="mr-1"/>{{ freight ? 'Pallet' : 'Package' }}
    </b-link>
  </div>

  <div class="table-responsive" v-else>
    <table class="table table-sm table-borderless mb-0 units">
      <thead class="small text-muted">
      <tr>
        <th class="qty">Qty</th>
        <th>L × W × H (in)</th>
        <th class="wt">Weight (lb)</th>
        <template v-if="freight">
          <th>Type</th>
          <th class="cls">Class</th>
          <th class="text-center">Stack</th>
        </template>
        <th></th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(r, i) in rows" :key="i">
        <td class="qty">
          <b-form-input size="sm" type="number" min="1" max="50" v-model.number="r.quantity"/>
        </td>
        <td>
          <div class="d-flex dims">
            <b-form-input size="sm" type="number" min="0" v-model.number="r.pkg_length" placeholder="L" :autofocus="i === 0"/>
            <b-form-input size="sm" type="number" min="0" v-model.number="r.pkg_width" placeholder="W"/>
            <b-form-input size="sm" type="number" min="0" v-model.number="r.pkg_height" placeholder="H"/>
          </div>
        </td>
        <td class="wt">
          <b-input-group size="sm">
            <b-form-input type="number" min="0" step="any" v-model.number="r.pkg_weight" @keydown.enter="enter(i)"/>
            <b-input-group-append v-if="scales.length">
              <b-dropdown size="sm" variant="outline-secondary" right>
                <template #button-content><font-awesome-icon icon="fa-solid fa-balance-scale"/></template>
                <b-dropdown-item v-for="s in scales" :key="s" @click="weigh(r, s)">Scale: {{ s }}</b-dropdown-item>
              </b-dropdown>
            </b-input-group-append>
          </b-input-group>
        </td>
        <template v-if="freight">
          <td>
            <b-form-select size="sm" v-model="r.handling_type" :options="lists.handling_types"/>
          </td>
          <td class="cls">
            <b-form-select size="sm" v-model="r.freight_class">
              <b-form-select-option :value="null">Auto{{ auto(r) ? ' (' + auto(r) + ')' : '' }}</b-form-select-option>
              <b-form-select-option v-for="c in lists.classes" :key="c" :value="c">{{ c }}</b-form-select-option>
            </b-form-select>
            <!-- always there, so typing the weight never shifts the row -->
            <div class="small text-muted text-nowrap density">{{ lbft(r) ? lbft(r) + ' lb/ft³' : '\u00a0' }}</div>
          </td>
          <td class="text-center align-middle">
            <b-form-checkbox v-model="r.stackable"/>
          </td>
        </template>
        <td class="align-middle text-right">
          <b-link class="text-danger" v-if="rows.length > 1" @click="remove(i)" title="Remove">
            <font-awesome-icon :icon="['far', 'trash-can']"/>
          </b-link>
        </td>
      </tr>
      </tbody>
    </table>
    <b-link class="small" @click="add">
      <font-awesome-icon icon="fa-solid fa-plus" class="mr-1"/>{{ freight ? 'Pallet' : 'Package' }}
    </b-link>
  </div>
</template>

<style scoped>
.units td, .units th {
  vertical-align: top;
  padding-left: 2px;
  padding-right: 2px;
}
.qty {
  width: 4.5em;
}
.wt {
  width: 8.5em;
}
/* fixed: "Auto (77.5)" and "Auto" must not resize the column as the weight is typed */
.cls {
  width: 8em;
  min-width: 8em;
  max-width: 8em;
}
.cls select {
  width: 100%;
}
.dims input {
  min-width: 3.5em;
}
.dims input + input {
  margin-left: 2px;
}
.unit-card {
  border: 1px solid var(--gray-300, #dee2e6);
  border-radius: 4px;
  padding: 8px;
}
.qty-group {
  width: 6.5em;
  flex: 0 0 6.5em;
}
</style>
